import { Injectable, computed, signal } from '@angular/core';
import { GlobalReportApiService } from './global-report-api.service';
import { ReportScopeService } from '../report-scope.service';
import { SalesTicketItem } from 'src/app/models/reports-v2/report-v2.types';

/** Page size for the infinite-scroll "Ventas" list. */
const PER_PAGE = 30;

interface SalesMeta {
  current_page?: number;
  last_page?: number;
  total?: number;
}

/**
 * State for the "Ventas" sub-tab of Grupo de ventas. Tickets are in the
 * thousands, so — unlike the by-location sub-tabs — they are NOT loaded all at
 * once: this pages through the dedicated `sales` endpoint with infinite scroll,
 * appending each page and deduping by `ticket_id`.
 */
@Injectable({ providedIn: 'root' })
export class SalesTicketsService {
  private readonly _eventId = signal<number | string | null>(null);
  private readonly _search = signal('');
  /** Scope `(rol, evento, búsqueda)` de lo cargado; `null` = nada cargado. */
  private readonly _loadedKey = signal<string | null>(null);

  readonly tickets = signal<SalesTicketItem[]>([]);
  readonly currentPage = signal(1);
  readonly lastPage = signal(1);
  readonly total = signal(0);
  readonly showLoadMore = signal(false);
  readonly isLoadingMore = signal(false);
  readonly loading = signal(false);

  readonly isEmpty = computed(
    () => this._loadedKey() !== null && this.tickets().length === 0
  );

  constructor(
    private globalReportApi: GlobalReportApiService,
    private reportScope: ReportScopeService,
  ) {}

  /**
   * Loads the first page. No-op if the current `(role, event, search)` scope is
   * already loaded or a request is in flight; call `reset()` first to force a
   * reload. The role is part of the scope because the backend narrows by it.
   *
   * Una página descartada por `loadMore` no se recarga desde acá: lo que hay en
   * pantalla sigue siendo válido para el scope con el que se cargó, y el próximo
   * `loadInitial` — que compara la clave contra el rol vigente — es el que pide
   * el reemplazo, sin vaciar la lista antes de tenerlo.
   */
  loadInitial(eventId: number | string, search: string = ''): void {
    if (this.loading()) {
      return;
    }

    const key = this.reportScope.keyFor(eventId, search);
    if (this._loadedKey() === key) {
      return;
    }

    this._eventId.set(eventId);
    this._search.set(search);
    this.loading.set(true);
    this.currentPage.set(1);
    this.lastPage.set(1);
    this.total.set(0);
    this.tickets.set([]);
    this._loadedKey.set(null);

    this.globalReportApi.getSales(eventId, 1, PER_PAGE, search).subscribe({
      next: ({ data = [], meta = {} }: { data?: SalesTicketItem[]; meta?: SalesMeta }) =>
        this.applyPage(data, meta, true, key),
      error: () => {
        this.tickets.set([]);
        this._loadedKey.set(key);
        this.loading.set(false);
      },
      complete: () => this.loading.set(false),
    });
  }

  loadMore(): void {
    const nextPage = this.currentPage() + 1;
    if (nextPage > this.lastPage()) {
      this.showLoadMore.set(false);
      return;
    }

    const eventId = this._eventId();
    if (eventId === null) {
      return;
    }

    // Scope con el que sale ESTE request. `getSales` manda el rol vigente, así
    // que una página pedida después de un cambio de rol pertenece a otro scope
    // que el de lo que ya está en pantalla: pegarlas juntas mezcla dos roles en
    // la misma lista y la marca como válida. Se compara al llegar la respuesta.
    const requestKey = this.reportScope.keyFor(eventId, this._search());

    this.isLoadingMore.set(true);

    this.globalReportApi
      .getSales(eventId, nextPage, PER_PAGE, this._search())
      .subscribe({
        next: ({ data = [], meta = {} }: { data?: SalesTicketItem[]; meta?: SalesMeta }) => {
          if (requestKey !== this._loadedKey()) {
            return;
          }

          this.applyPage(data, meta, false, requestKey);
        },
        error: () => this.isLoadingMore.set(false),
        complete: () => this.isLoadingMore.set(false),
      });
  }

  private applyPage(
    data: SalesTicketItem[],
    meta: SalesMeta,
    replace: boolean,
    key: string | null
  ): void {
    if (replace) {
      this.tickets.set(data);
    } else {
      const currentIds = new Set(this.tickets().map((t) => t.ticket_id));
      this.tickets.set([
        ...this.tickets(),
        ...data.filter((t) => !currentIds.has(t.ticket_id)),
      ]);
    }

    this.currentPage.set(meta.current_page ?? 1);
    this.lastPage.set(meta.last_page ?? 1);
    if (meta.total !== undefined) {
      this.total.set(meta.total);
    }
    this.showLoadMore.set((meta.current_page ?? 1) < (meta.last_page ?? 1));
    this._loadedKey.set(key);
  }

  reset(): void {
    this._eventId.set(null);
    this._search.set('');
    this._loadedKey.set(null);
    this.tickets.set([]);
    this.currentPage.set(1);
    this.lastPage.set(1);
    this.total.set(0);
    this.showLoadMore.set(false);
    this.isLoadingMore.set(false);
    this.loading.set(false);
  }
}
